import React from 'react';
import {Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {getWorkbooks} from '../../../api/workbooks';
import {Page} from '../../layout/Pages';
import jwtDecode from 'jwt-decode';

export default function AllWorkbooks() {
    // Get tenant ID from JWT token for cache key
    const getTenantId = () => {
        try {
            const accessToken = localStorage.getItem('access_token');
            if (accessToken) {
                const decoded = jwtDecode(accessToken);
                return decoded.tenant_id || decoded.tenant || null;
            }
        } catch (e) {
            console.error('Error decoding token:', e);
        }
        return null;
    };

    const tenantId = getTenantId();

    // Include tenant ID in query key to ensure proper cache isolation per tenant
    const {data: workbooks, isLoading, error} = useQuery(
        ['workbooks', tenantId],
        getWorkbooks
    );

    if (isLoading) {
        return (
            <Page title="All Workbooks">
                <div className="flex justify-center items-center py-12">
                    <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-3 text-gray-600">Loading workbooks...</span>
                </div>
            </Page>
        );
    }

    if (error) {
        return (
            <Page title="All Workbooks">
                <div className="text-center py-12">
                    <div className="text-red-600 mb-2">Failed to load workbooks</div>
                    <p className="text-sm text-gray-500">{error.message}</p>
                </div>
            </Page>
        );
    }

    // Check if we're in an embed context
    const isEmbed = window.location.pathname.startsWith('/embed');

    // Generate the appropriate link for each workbook
    const getWorkbookLink = (workbookId) => {
        if (isEmbed) {
            // Preserve query params for embed (wp_token, tenant_id)
            const searchParams = window.location.search;
            return `/embed/${workbookId}${searchParams}`;
        }
        return `/workbooks/${workbookId}`;
    };

    return (
        <Page title="All Workbooks">
            {workbooks && workbooks.length > 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                        {workbooks.map(workbook => (
                            <li key={workbook.id}>
                                <Link
                                    to={getWorkbookLink(workbook.id)}
                                    className="block hover:bg-gray-50 transition-colors"
                                >
                                    <div className="px-4 py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-medium ">
                                                    {workbook.title}
                                                </h3>
                                            </div>
                                            <div className="ml-4 flex-shrink-0">
                                                <svg
                                                    className="h-5 w-5 text-gray-400"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="text-center py-12">
                    <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No workbooks</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by creating a new workbook.</p>
                </div>
            )}
        </Page>
    );
}

